import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  TextField, 
  Button, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  useTheme,
  alpha,
  Stack,
  InputAdornment,
  Avatar,
  AlertTitle,
  IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DatePicker from "react-multi-date-picker";
import { format, differenceInDays, addDays, isBefore, startOfDay } from 'date-fns';
import ExploreIcon from '@mui/icons-material/Explore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import InterestsIcon from '@mui/icons-material/Interests';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SendIcon from '@mui/icons-material/Send';
import { motion } from 'framer-motion';
import './TripForm.css'; // Import custom CSS
import TokenDisplay from './TokenDisplay'; // Import TokenDisplay component
import { searchCities } from '../services/citiesService';
import { useAuth } from '../contexts/AuthContext';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

const interests = [
  'Adventure', 'Culture', 'Food & Dining', 'History', 'Nature',
  'Shopping', 'Relaxation', 'Photography', 'Art & Museums', 'Nightlife'
];

const popularDestinations = [
  '✨ Enter Custom Destination',
  'Paris, France',
  'Tokyo, Japan',
  'New York, USA',
  'Rome, Italy',
  'Bali, Indonesia',
  'London, UK',
  'Sydney, Australia',
  'Barcelona, Spain',
  'Dubai, UAE',
  'Cancun, Mexico',
  'Santorini, Greece',
  'Kyoto, Japan',
  'Amsterdam, Netherlands',
  'Cape Town, South Africa',
  'Machu Picchu, Peru',
];

export const TripForm = ({ onSubmit, initialValues }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [formData, setFormData] = useState(initialValues || {
    destination: '',
    startDate: null,
    endDate: null,
    adults: 2,
    children: 0,
    interests: [],
    budget: 'medium'
  });

  const [touched, setTouched] = useState({
    destination: false,
    startDate: false,
    endDate: false,
    adults: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [loadingDestinations, setLoadingDestinations] = useState(false);

  // Add a state for token refresh trigger that only updates when needed
  const [tokenRefreshTrigger, setTokenRefreshTrigger] = useState(Date.now());

  // Get current date at midnight (start of day) to use for date validation
  const today = startOfDay(new Date());

  // Add ref for the date picker
  const datePickerRef = React.useRef();

  // Validate form and update error state
  const validateForm = () => {
    const errors = {};
    
    if (!formData.destination) {
      errors.destination = 'Destination is required';
    }
    
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (!formData.endDate) {
      errors.endDate = 'End date is required';
    }
    
    if (formData.adults < 1) {
      errors.adults = 'At least 1 adult is required';
    }
    
    // Validate date range doesn't exceed a week
    if (formData.startDate instanceof Date && formData.endDate instanceof Date) {
      const daysDifference = differenceInDays(formData.endDate, formData.startDate);
      if (daysDifference > 14) {
        errors.dateRange = 'Trip duration cannot exceed 14 days';
      }
    }
    
    setFormErrors(errors);
    return errors;
  };

  const handleDestinationChange = (event, newValue) => {
    // Handle the destination selection
    setFormData({ 
      ...formData, 
      destination: typeof newValue === 'string' ? newValue : newValue?.display || '' 
    });
    setTouched({ ...touched, destination: true });
    validateForm();
  };

  // Handle destination search
  const handleDestinationSearch = async (event, query) => {
    if (!query) {
      setDestinationOptions([]);
      return;
    }

    setLoadingDestinations(true);
    try {
      const results = await searchCities(query);
      
      // Filter to ensure unique destinations by display name
      const uniqueResults = Array.from(
        new Map(
          results.map(item => [
            typeof item === 'string' ? item : item.display, 
            item
          ])
        ).values()
      );
      
      setDestinationOptions(uniqueResults);
    } catch (error) {
      console.error('Error searching cities:', error);
      setDestinationOptions([]);
    } finally {
      setLoadingDestinations(false);
    }
  };

  const handleDateRangeChange = (dateValues) => {
    console.log("DateRangeChange raw value:", dateValues);
    
    if (!dateValues || (Array.isArray(dateValues) && dateValues.length === 0)) {
      setFormData({
        ...formData,
        startDate: null,
        endDate: null
      });
      setTouched({
        ...touched,
        startDate: true,
        endDate: true
      });
      console.log("Clearing date selections");
      return;
    }
    
    try {
      // Handle range selection (array with two dates)
      if (Array.isArray(dateValues) && dateValues.length === 2 && dateValues[0] && dateValues[1]) {
        console.log("Processing date range:", dateValues);
        
        // Convert to JavaScript Date objects
        let startDate, endDate;
        
        // Handle first date - convert from DateObject format
        if (dateValues[0]) {
          try {
            if (typeof dateValues[0] === 'string') {
              startDate = new Date(dateValues[0]);
            } else if (dateValues[0] instanceof Date) {
              startDate = dateValues[0];
            } else if (dateValues[0].unix) {
              // Convert unix timestamp (seconds) to milliseconds
              startDate = new Date(dateValues[0].unix * 1000);
            } else if (dateValues[0].toDate) {
              // If it has a toDate method, use it
              startDate = dateValues[0].toDate();
            } else {
              // For DateObject from react-multi-date-picker
              const dateObj = dateValues[0];
              startDate = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
            }
          } catch (e) {
            console.error("Error parsing start date:", e);
            startDate = new Date();
          }
        }
        
        // Handle second date - convert from DateObject format
        if (dateValues[1]) {
          try {
            if (typeof dateValues[1] === 'string') {
              endDate = new Date(dateValues[1]);
            } else if (dateValues[1] instanceof Date) {
              endDate = dateValues[1];
            } else if (dateValues[1].unix) {
              // Convert unix timestamp (seconds) to milliseconds
              endDate = new Date(dateValues[1].unix * 1000);
            } else if (dateValues[1].toDate) {
              // If it has a toDate method, use it
              endDate = dateValues[1].toDate();
            } else {
              // For DateObject from react-multi-date-picker
              const dateObj = dateValues[1];
              endDate = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
            }
          } catch (e) {
            console.error("Error parsing end date:", e);
            endDate = new Date();
          }
        }
        
        console.log("Converted range dates:", startDate, endDate);
        
        if (startDate && endDate) {
          // Check if the date range exceeds 14 days
          const daysDifference = differenceInDays(endDate, startDate);
          console.log("Days difference:", daysDifference);
          
          if (daysDifference > 14) {
            // If exceeds 14 days, adjust the end date to be 14 days from start date
            endDate = addDays(startDate, 14);
            console.log("Adjusted end date to 14-day maximum:", endDate);
          }
          
          setFormData({
            ...formData,
            startDate,
            endDate
          });
          
          // Mark date fields as touched
          setTouched({
            ...touched,
            startDate: true,
            endDate: true
          });
          
          // Close the calendar using the ref
          if (datePickerRef.current) {
            datePickerRef.current.closeCalendar();
          }
          
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error("Error in handleDateRangeChange:", error);
      return false;
    }
  };

  const handleDateChange = (field, newValue) => {
    // Don't allow past dates
    if (newValue && isBefore(newValue, today) && field === 'startDate') {
      // If selected date is in the past, use today instead
      newValue = today;
    }
    
    setFormData({ ...formData, [field]: newValue });
    setTouched({ ...touched, [field]: true });
    
    // If setting start date and it's after end date, update end date
    if (field === 'startDate' && formData.endDate && newValue > formData.endDate) {
      setFormData(prev => ({ 
        ...prev, 
        [field]: newValue, 
        endDate: addDays(newValue, 3) 
      }));
    }
    
    // If setting end date and it's before start date, update start date
    if (field === 'endDate' && formData.startDate && newValue < formData.startDate) {
      setFormData(prev => ({ 
        ...prev, 
        [field]: newValue, 
        startDate: addDays(newValue, -3) 
      }));
    }
    
    // Validate after change
    setTimeout(() => validateForm(), 100);
  };

  const handleNumberChange = (field, e) => {
    setFormData({ ...formData, [field]: parseInt(e.target.value) });
    if (field === 'adults') {
      setTouched({ ...touched, adults: true });
    }
    
    // Validate after change
    validateForm();
  };

  // Add new incrementer function for plus/minus buttons
  const handleIncrement = (field, amount) => {
    let newValue;
    
    if (field === 'adults') {
      // Adults must be at least 1 and maximum 10
      newValue = Math.min(Math.max(formData.adults + amount, 1), 10);
      setTouched({ ...touched, adults: true });
    } else if (field === 'children') {
      // Children must be at least 0 and maximum 10
      newValue = Math.min(Math.max(formData.children + amount, 0), 10);
    }
    
    setFormData({ ...formData, [field]: newValue });
    
    // Validate after change
    validateForm();
  };

  const handleInterestsChange = (e) => {
    setFormData({ ...formData, interests: e.target.value });
  };

  const handleBudgetChange = (e) => {
    setFormData({ ...formData, budget: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {
      destination: true,
      startDate: true,
      endDate: true,
      adults: true
    };
    setTouched(allTouched);
    
    // Validate form and check for errors
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setError('Please fill in all required fields');
      return;
    }
    
    setError(null);
    setLoading(true);

    try {
      await onSubmit(formData);
      // Update token refresh trigger after submission
      setTokenRefreshTrigger(Date.now());
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  // Create custom input component for the date picker that matches your design
  const DateRangePickerInput = ({ openCalendar, value }) => {
    return (
      <TextField
        label="Travel Dates"
        variant="outlined"
        required
        fullWidth
        value={value}
        onClick={openCalendar}
        placeholder="Select travel dates"
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthIcon color="primary" />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            background: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(5px)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            '&.Mui-focused': {
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.65)',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.85rem',
            fontWeight: 500,
            color: alpha(theme.palette.primary.main, 0.8),
            letterSpacing: '0.01em',
            transition: 'color 0.2s ease, transform 0.2s ease, font-weight 0.2s ease',
            '&.Mui-focused': {
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
            '&.MuiFormLabel-filled': {
              fontWeight: 500,
            },
          },
          '& .MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.85)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            paddingLeft: '4px',
            paddingRight: '4px',
            borderRadius: '2px',
            boxShadow: '0 2px 4px rgba(94, 23, 235, 0.05)',
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.7rem',
            marginTop: '4px',
            fontWeight: 400,
            transition: 'color 0.2s ease',
            color: alpha(theme.palette.text.secondary, 0.8),
            '&.Mui-error': {
              color: theme.palette.error.main,
              fontWeight: 500,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.8)',
            transition: 'border-color 0.2s ease',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.primary.main, 0.5),
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            borderColor: theme.palette.primary.main,
          },
          '& .MuiInputBase-input': {
            fontSize: '0.85rem',
            fontWeight: 500,
            color: theme.palette.text.primary,
            padding: '12px 14px 12px 0',
            '&::placeholder': {
              color: alpha(theme.palette.text.secondary, 0.6),
              opacity: 1,
            },
          },
        }}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={4} 
        sx={{ 
          p: { xs: 3, md: 4 }, 
          borderRadius: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(25px)',
          boxShadow: '0 16px 40px rgba(94, 23, 235, 0.15), 0 4px 12px rgba(31, 38, 135, 0.05)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(94, 23, 235, 0.08) 0%, rgba(255, 255, 255, 0.03) 50%, rgba(255, 255, 255, 0) 100%)',
            zIndex: 0,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(94, 23, 235, 0.15) 0%, rgba(94, 23, 235, 0) 70%)',
            zIndex: 0,
            pointerEvents: 'none',
          },
          border: '1px solid rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
          borderRight: '1px solid rgba(255, 255, 255, 0.5)',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* Only show TokenDisplay if user is logged in */}
        {user && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 32, md: 36 },  // Adjusted top spacing
              right: { xs: 12, md: 16 }, // Adjusted right spacing
              zIndex: 100,
              transform: 'scale(0.85)', // Make it slightly smaller
              transformOrigin: 'top right', // Scale from top right
              '& .MuiChip-root': { // Target the chip component inside TokenDisplay
                height: '28px', // Reduce height
                '& .MuiChip-label': {
                  fontSize: '0.8rem', // Smaller font size
                  px: 1.5, // Reduce horizontal padding
                },
                '& .MuiChip-icon': {
                  fontSize: '1.1rem', // Smaller icon
                  marginLeft: '6px', // Adjust icon spacing
                },
              }
            }}
          >
            <TokenDisplay refreshTrigger={tokenRefreshTrigger} />
          </Box>
        )}

        {/* Section Title */}
        <Box sx={{ mb: 4, position: 'relative', zIndex: 1, mt: { xs: 1, md: 0.5 } }}> {/* Added top margin */}
          <Typography 
            variant="h4" 
            component="h2" 
            fontWeight={700}
            sx={{ 
              mb: 1.5, 
              background: 'linear-gradient(135deg, #15192E 0%, #5E17EB 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: '60px',
                height: '4px',
                background: 'linear-gradient(90deg, rgba(94, 23, 235, 0.9) 0%, rgba(94, 23, 235, 0.3) 100%)',
              }
            }}
          >Trip Details
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: '95%', opacity: 0.9 }}>
            Let our AI travel expert create a personalized day-by-day itinerary based on your preferences, interests, and travel style.
          </Typography>
        </Box>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Destination */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={destinationOptions}
                getOptionLabel={(option) => 
                  typeof option === 'string' ? option : option.display
                }
                filterOptions={(x) => x}  // Disable built-in filtering since we handle it in handleDestinationSearch
                value={formData.destination}
                onChange={handleDestinationChange}
                onInputChange={handleDestinationSearch}
                loading={loadingDestinations}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination"
                    variant="outlined"
                    required
                    error={touched.destination && !formData.destination}
                    helperText={touched.destination && !formData.destination 
                      ? "Destination is required" 
                      : "Type a city name"
                    }
                    placeholder="Type a city name"
                    fullWidth
                    InputLabelProps={{
                      sx: {
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        color: alpha(theme.palette.primary.main, 0.8),
                        letterSpacing: '0.01em',
                        transition: 'color 0.2s ease, transform 0.2s ease, font-weight 0.2s ease',
                        '&.Mui-focused': {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        },
                        '&.MuiFormLabel-filled': {
                          fontWeight: 500,
                        },
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.85)',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          paddingLeft: '4px',
                          paddingRight: '4px',
                          borderRadius: '2px',
                          boxShadow: '0 2px 4px rgba(94, 23, 235, 0.05)',
                        },
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.5),
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: theme.palette.primary.main,
                        }
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.8)',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        '&::placeholder': {
                          color: alpha(theme.palette.text.secondary, 0.6),
                          opacity: 1,
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        marginTop: '4px',
                        fontWeight: 400,
                        transition: 'color 0.2s ease',
                        color: alpha(theme.palette.text.secondary, 0.8),
                        '&.Mui-error': {
                          color: theme.palette.error.main,
                          fontWeight: 500,
                        },
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <ExploreIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingDestinations ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Date Range Picker */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  borderRadius: 0,
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  padding: '0',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 2px 4px rgba(94, 23, 235, 0.05)',
                  },
                  '& .custom-calendar': {
                    '--rmdp-shadow': 'none',
                    '--rmdp-secondary-color': alpha(theme.palette.primary.main, 0.7),
                    '--rmdp-primary-color': theme.palette.primary.main,
                    '--rmdp-primary-opacity': '0.25',
                    '--rmdp-secondary-opacity': '0.15',
                    '--rmdp-shadow-color': 'rgba(94, 23, 235, 0.15)',
                    '--rmdp-today-color': theme.palette.primary.main,
                    '--rmdp-hover-bg': alpha(theme.palette.primary.main, 0.1),
                    'border': 'none',
                    'width': '100%',
                    'background': 'transparent',
                    'color': theme.palette.text.primary,
                    '.rmdp-container': {
                      zIndex: 1500,
                    },
                    '.rmdp-calendar': {
                      zIndex: 1500,
                      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.8)',
                    },
                    '.rmdp-header-values': {
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    },
                    '.rmdp-arrow': {
                      border: 'solid ' + theme.palette.primary.main,
                    },
                    '.rmdp-week-day': {
                      color: alpha(theme.palette.text.primary, 0.7),
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    },
                    '.rmdp-day': {
                      color: theme.palette.text.primary,
                      span: {
                        borderRadius: 0,
                        transition: 'all 0.2s ease',
                      },
                      '&.rmdp-selected span:not(.highlight)': {
                        background: theme.palette.primary.main,
                        color: 'white',
                        boxShadow: '0 4px 8px ' + alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.rmdp-today span': {
                        border: '1px solid ' + theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      },
                      '&.rmdp-range span': {
                        background: alpha(theme.palette.primary.main, 0.1),
                      },
                    },
                    '.rmdp-action-button': {
                      color: theme.palette.primary.main,
                      background: 'none',
                      border: '1px solid ' + alpha(theme.palette.primary.main, 0.5),
                      boxShadow: 'none',
                      borderRadius: 0,
                      padding: '5px 10px',
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.1),
                      },
                    },
                  }
                }}
              >
                <DatePicker
                  ref={datePickerRef}
                  value={[
                    formData.startDate instanceof Date ? formData.startDate : null,
                    formData.endDate instanceof Date ? formData.endDate : null
                  ].filter(Boolean)}
                  onChange={(dates) => {
                    handleDateRangeChange(dates);
                  }}
                  range
                  minDate={new Date(new Date().setHours(0,0,0,0))}
                  format="MMM DD, YYYY"
                  render={<DateRangePickerInput />}
                  highlightToday={true}
                  weekDays={["S", "M", "T", "W", "T", "F", "S"]}
                  calendarPosition="bottom-center"
                  fixMainPosition={true}
                  shadow={false}
                  hideWeekDays={false}
                  className="custom-calendar"
                  animation
                  portal
                  zIndex={1500}
                  onOpen={() => {
                    console.log("Calendar opened with current dates:", formData.startDate, formData.endDate);
                  }}
                  onClose={() => {
                    console.log("Calendar closed with dates:", formData.startDate, formData.endDate);
                    // If we have only a start date and no end date after closing, duplicate the start date as end date
                    if (formData.startDate instanceof Date && !formData.endDate) {
                      setFormData(prev => ({
                        ...prev,
                        endDate: new Date(prev.startDate)
                      }));
                      setTouched(prev => ({
                        ...prev,
                        endDate: true
                      }));
                      console.log("Auto-setting end date to match start date");
                      setTimeout(() => validateForm(), 100);
                    }
                  }}
                  style={{
                    width: "100%",
                    height: "100%"
                  }}
                />
              </Box>
              {/* Add helper text for date range */}
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  mt: 0.5,
                  ml: 2,
                  color: formErrors.dateRange ? theme.palette.error.main : alpha(theme.palette.text.secondary, 0.8),
                  fontWeight: formErrors.dateRange ? 500 : 400,
                  fontSize: '0.75rem'
                }}
              >
                {formErrors.dateRange || "Trip duration cannot exceed 14 days"}
              </Typography>
            </Grid>

            {/* Travelers */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      borderRadius: 0,
                      background: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(5px)',
                      border: '1px solid rgba(255, 255, 255, 0.8)',
                      p: 1.5,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.65)',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <GroupIcon color="primary" sx={{ mr: 0.8, fontSize: '1rem' }} />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          color: alpha(theme.palette.primary.main, 0.8),
                          display: 'flex',
                          alignItems: 'center',
                          letterSpacing: '0.01em',
                        }}
                      >
                        Adults
                        {touched.adults && formData.adults < 1 && (
                          <Typography 
                            component="span" 
                            sx={{ 
                              ml: 0.5, 
                              color: theme.palette.error.main,
                              fontSize: '0.65rem',
                              fontWeight: 500
                            }}
                          >
                            (required)
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconButton
                        onClick={() => handleIncrement('adults', -1)}
                        disabled={formData.adults <= 1}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: '28px',
                          height: '28px',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                          '&.Mui-disabled': {
                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                            color: alpha(theme.palette.text.disabled, 0.5),
                          }
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      
                      <Typography
                        variant="h6"
                        sx={{
                          mx: 1,
                          minWidth: '30px',
                          textAlign: 'center',
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {formData.adults}
                      </Typography>
                      
                      <IconButton
                        onClick={() => handleIncrement('adults', 1)}
                        disabled={formData.adults >= 10}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: '28px',
                          height: '28px',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                          '&.Mui-disabled': {
                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                            color: alpha(theme.palette.text.disabled, 0.5),
                          }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  {touched.adults && formData.adults < 1 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.error.main,
                        mt: 0.5,
                        ml: 1,
                        fontSize: '0.7rem',
                        display: 'block'
                      }}
                    >
                      At least 1 adult is required
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      borderRadius: 0,
                      background: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(5px)',
                      border: '1px solid rgba(255, 255, 255, 0.8)',
                      p: 1.5,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.65)',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <GroupIcon color="primary" sx={{ mr: 0.8, fontSize: '1rem' }} />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          color: alpha(theme.palette.primary.main, 0.8),
                          letterSpacing: '0.01em',
                        }}
                      >
                        Children
                      </Typography>
                    </Box>
                    
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconButton
                        onClick={() => handleIncrement('children', -1)}
                        disabled={formData.children <= 0}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: '28px',
                          height: '28px',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                          '&.Mui-disabled': {
                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                            color: alpha(theme.palette.text.disabled, 0.5),
                          }
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      
                      <Typography
                        variant="h6"
                        sx={{
                          mx: 1,
                          minWidth: '30px',
                          textAlign: 'center',
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {formData.children}
                      </Typography>
                      
                      <IconButton
                        onClick={() => handleIncrement('children', 1)}
                        disabled={formData.children >= 10}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: '28px',
                          height: '28px',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                          '&.Mui-disabled': {
                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                            color: alpha(theme.palette.text.disabled, 0.5),
                          }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Interests */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Interests"
                value={Array.isArray(formData.interests) ? formData.interests.join(', ') : formData.interests}
                onChange={handleInterestsChange}
                fullWidth
                placeholder="I want to see nature, stay in luxury hotels, enjoy local cuisine..."
                helperText="Feel free to write detailed preferences like 'I want to relax in a nice hotel' or 'I prefer cultural activities'"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InterestsIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    background: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(5px)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    '&.Mui-focused': {
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.65)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: alpha(theme.palette.primary.main, 0.8),
                    letterSpacing: '0.01em',
                    transition: 'color 0.2s ease, transform 0.2s ease, font-weight 0.2s ease',
                    '&.Mui-focused': {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                    '&.MuiFormLabel-filled': {
                      fontWeight: 500,
                    },
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.85)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    borderRadius: '2px',
                    boxShadow: '0 2px 4px rgba(94, 23, 235, 0.05)',
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.7rem',
                    marginTop: '4px',
                    fontWeight: 400,
                    transition: 'color 0.2s ease',
                    color: alpha(theme.palette.text.secondary, 0.8),
                    '&.Mui-error': {
                      color: theme.palette.error.main,
                      fontWeight: 500,
                    },
                  },
                }}
              />
            </Grid>

            {/* Budget */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Budget"
                value={formData.budget}
                onChange={handleBudgetChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    background: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(5px)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    '&.Mui-focused': {
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.65)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: alpha(theme.palette.primary.main, 0.8),
                    letterSpacing: '0.01em',
                    transition: 'color 0.2s ease, transform 0.2s ease, font-weight 0.2s ease',
                    '&.Mui-focused': {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                    '&.MuiFormLabel-filled': {
                      fontWeight: 500,
                    },
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.85)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    borderRadius: '2px',
                    boxShadow: '0 2px 4px rgba(94, 23, 235, 0.05)',
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.7rem',
                    marginTop: '4px',
                    fontWeight: 400,
                    transition: 'color 0.2s ease',
                    color: alpha(theme.palette.text.secondary, 0.8),
                    '&.Mui-error': {
                      color: theme.palette.error.main,
                      fontWeight: 500,
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                    transition: 'border-color 0.2s ease',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: theme.palette.primary.main,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    padding: '12px 14px 12px 0',
                    '&::placeholder': {
                      color: alpha(theme.palette.text.secondary, 0.6),
                      opacity: 1,
                    },
                  },
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                  },
                }}
              >
                <MenuItem value="budget">Budget</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="luxury">Luxury</MenuItem>
              </TextField>
            </Grid>

            {/* Error Message */}
            {error && (
              <Grid item xs={12}>
                <Alert 
                  severity="error" 
                  sx={{ 
                    borderRadius: 0,
                    background: 'rgba(255, 81, 81, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.dark,
                    fontWeight: 500,
                  }}
                >
                  {error}
                </Alert>
              </Grid>
            )}

            {/* Submit Button */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                endIcon={loading ? undefined : <SendIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 0,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  border: 'none',
                  boxShadow: '0 8px 24px rgba(94, 23, 235, 0.25)',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(94, 23, 235, 0.35)',
                    filter: 'brightness(1.05)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 0) 100%)',
                    zIndex: 0,
                    pointerEvents: 'none',
                  },
                }}
              >
                Generate My Itinerary
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </LocalizationProvider>
  );
}; 